import { ReactNode } from "react";
import { Box, Stack } from "@mui/material";

import { PlusIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useListSubjectsSubjectsGetQuery } from "api";
import { can } from "authorization/authorization.util";
import { SubjectPermissionEnum } from "authorization/types";
import { DynamicButton } from "components/DynamicButton/DynamicButton";
import { ErrorPage, LoadingPage, NoDataPage } from "components/pages";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { IZone } from "interfaces/zone.interface";
import { mapById } from "utils/mapById";

import { SubjectList } from "./components/SubjectList";

import { PageHeader } from "../components/PageHeader";

type WrapperProps = {
  children: ReactNode;
  title: string;
  zoneId: string;
};

function Wrapper({ children, title, zoneId }: WrapperProps) {
  const { t } = useTranslation();
  const { permissions } = useAuthenticatedUser();

  const canCreateSubject = can(
    permissions,
    SubjectPermissionEnum["subject.create"],
  );

  return (
    <Stack minHeight={"100vh"} gap={2}>
      <PageHeader title={title}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          {canCreateSubject && (
            <DynamicButton
              href={`/administration/zones/${zoneId}/subjects/create`}
              startIcon={<PlusIcon />}
              variant={"text"}
            >
              {t("Create Subject")}
            </DynamicButton>
          )}
          <Box flexGrow={1} />
        </Stack>
      </PageHeader>
      {children}
    </Stack>
  );
}

type SubjectListPageProps = {
  zone: IZone;
  zones: IZone[];
};

export const SubjectListPage = ({ zone, zones }: SubjectListPageProps) => {
  const { t } = useTranslation();

  const {
    data: subjectData,
    isLoading,
    isError,
    refetch,
  } = useListSubjectsSubjectsGetQuery({
    path: `${zone.path}.*`,
    embed: ["devices"],
  });

  const subjectList = subjectData?.data ?? [];

  if (isLoading) {
    return (
      <Wrapper title={zone.name} zoneId={zone.id}>
        <LoadingPage />
      </Wrapper>
    );
  }

  if (isError) {
    return (
      <Wrapper title={zone.name} zoneId={zone.id}>
        <ErrorPage
          title={"Failed to load subjects"}
          buttonOnClick={() => refetch()}
          buttonText={t("Try again")}
        />
      </Wrapper>
    );
  }

  if (subjectList.length === 0) {
    return (
      <Wrapper title={zone.name} zoneId={zone.id}>
        <NoDataPage
          message={t("there_are_no_subjects_in_this_zone")}
          title={zone.name}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper title={zone.name} zoneId={zone.id}>
      <SubjectList subjects={subjectList} zoneEntity={mapById(zones)} />
    </Wrapper>
  );
};
